import type {
  BlocklistCategoriesResponse,
  BlocklistListResponse,
  BlocklistQueryParams,
  CreateBlocklistRequest,
  CreateBlocklistResponse,
  DeactivateBlocklistRequest,
  DeactivateBlocklistResponse,
} from '@/features/onboarding/types';
import { API_ROUTES } from '@/shared/constants/api.constants';
import { apiClient, handleAPIError } from '@/shared/utils/api/client';

/**
 * Blocklist Service
 * Handles all blocklist-related API calls
 */
export const blocklistService = {
  /**
   * Get paginated list of blocklist items
   * @param params - Query parameters (page, limit, search, property, isActive)
   * @returns Promise with blocklist response
   */
  getList: async (params: BlocklistQueryParams = {}): Promise<BlocklistListResponse> => {
    try {
      const queryParams = new URLSearchParams();

      if (params.page) queryParams.append('page', params.page.toString());
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.search) queryParams.append('search', params.search);
      if (params.property) queryParams.append('property', params.property);
      if (params.isActive !== undefined) queryParams.append('isActive', params.isActive.toString());

      const queryString = queryParams.toString();
      const url = queryString
        ? `${API_ROUTES.BLOCKLIST.LIST}?${queryString}`
        : API_ROUTES.BLOCKLIST.LIST;

      const response = await apiClient.get<BlocklistListResponse>(url);
      return response.data;
    } catch (error) {
      throw handleAPIError(error);
    }
  },

  /**
   * Create a new blocklist item
   * @param data - Blocklist item data
   * @returns Promise with created blocklist item
   */
  create: async (data: CreateBlocklistRequest): Promise<CreateBlocklistResponse> => {
    try {
      const response = await apiClient.post<CreateBlocklistResponse>(
        API_ROUTES.BLOCKLIST.LIST,
        data
      );
      return response.data;
    } catch (error) {
      throw handleAPIError(error);
    }
  },

  /**
   * Deactivate a blocklist item
   * @param id - Blocklist item ID
   * @param reason - Reason for deactivation
   * @returns Promise with updated blocklist item
   */
  deactivate: async ({
    id,
    reason,
  }: DeactivateBlocklistRequest): Promise<DeactivateBlocklistResponse> => {
    try {
      const response = await apiClient.patch<DeactivateBlocklistResponse>(
        `${API_ROUTES.BLOCKLIST.LIST}/${id}`,
        { reason }
      );
      return response.data;
    } catch (error) {
      throw handleAPIError(error);
    }
  },

  /**
   * Get blocklist categories
   * @returns Promise with list of categories
   */
  getCategories: async (): Promise<BlocklistCategoriesResponse> => {
    try {
      const response = await apiClient.get<BlocklistCategoriesResponse>(
        API_ROUTES.BLOCKLIST.CATEGORIES
      );
      return response.data;
    } catch (error) {
      throw handleAPIError(error);
    }
  },
};
